/**
 * Pick the image that should represent a listing — page hero and social card.
 *
 * The naive `coverImage || images[0]` put a **floor plan** on both the hero and
 * the Facebook preview for at least one live listing. A floor plan is a fine
 * gallery asset and a terrible first impression; buyers scrolling a feed decide
 * on the exterior shot.
 *
 * Detection is by filename, caption and URL, because the Gallery schema has no
 * explicit "this is a document" flag. Heuristic, but the failure mode is mild:
 * a mislabelled photo simply drops to second choice, and if every image looks
 * like a document we fall back to the original behaviour rather than showing
 * nothing.
 *
 * Shared by client/src/pages/PropertyPage.js and client/api/property-og.js so
 * the hero and the share card can never disagree about which photo represents
 * the home.
 */

const DOCUMENT_HINT =
  /(floor[\s._-]?plan|floorplan|site[\s._-]?plan|survey|blueprint|schematic|measure|dimension|disclosure|brochure|flyer|\.pdf)/i;

function looksLikeDocument(image) {
  if (!image) return false;
  const haystack = [image.filename, image.caption, image.url].filter(Boolean).join(' ');
  return DOCUMENT_HINT.test(haystack);
}

export function pickHeroImage(gallery, settings) {
  const g = gallery || {};

  // An explicit choice always wins — but only if that image is still in the
  // gallery, so a deleted photo can't leave the hero pointing at nothing.
  const chosen = settings && settings.heroImageUrl;
  if (chosen) {
    const stillPresent =
      (Array.isArray(g.images) && g.images.some(i => i && i.url === chosen)) ||
      (g.coverImage && g.coverImage.url === chosen);
    if (stillPresent) return chosen;
  }

  const images = Array.isArray(g.images) ? g.images : [];
  const photos = images.filter(i => i && i.url && i.mediaType !== 'video');

  // An explicitly chosen cover wins — unless it's a document.
  if (g.coverImage && g.coverImage.url && !looksLikeDocument(g.coverImage)) {
    return g.coverImage.url;
  }

  const firstRealPhoto = photos.find(i => !looksLikeDocument(i));
  if (firstRealPhoto) return firstRealPhoto.url;

  // Everything reads as a document — better a floor plan than a blank card.
  return (g.coverImage && g.coverImage.url) || (photos[0] && photos[0].url) || '';
}

export { looksLikeDocument };
export default pickHeroImage;
